import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Form } from "antd";
import { useIntl } from "react-intl";

import { useSelector, useDispatch } from "react-redux";

import { useHistory } from "react-router-dom";

import { changeProfileServiceProvider } from "../../../redux/modules/profile/actions";
import {
  getListCountries,
  getCep,
} from "../../../redux/modules/system/actions";

import useNotification from "../../../hooks/newUXNotification";
import { useLoading } from "../../../hooks/loading";

import cnpjCpfFormatter from "../../../utils/cnpjCpfFormatter";

import {
  getCountryFromTelephoneNumber,
  getFullTelephoneNumber,
  removeCountryFromTelephoneNumber,
} from "../../../newUXComponents/TelephoneInput";

import useProvisionTypes from "../../../hooks/useProvisionTypes";
import { OTHERS_SERVICE_TYPE_ID } from "../../../constants/serviceProviderTypes";
import taxes from "../../../constants/taxes";
import types from "../../../constants/types";

import Button from "../../../newUXComponents/Button";

import { numberMask } from "../../../utils/numberMask";

import FirstForm from "./firstForm";
import SecondForm from "./secondForm";
import ChangeForm from "./changeForm";

import { INITIAL_VALUES } from "./validations";

import {
  ContentForm,
  ContainerBtn,
  CustomSteps,
  StepsContent,
  StepsAction,
} from "./styles";

import { Container, Title, SubTitle, Hr } from "../styles";

const FormAccountServiceProvider = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();

  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const notification = useNotification();
  const { handleLoading } = useLoading();

  const { provisionTypes } = useProvisionTypes();

  const optionsProvision = useMemo(
    () =>
      [...provisionTypes].sort((a, b) => {
        if (a?.value === 0) return -1;
        if (b?.value === 0) return 1;
        return a.label.localeCompare(b.label);
      }),
    [provisionTypes]
  );

  const { accountCreated, user } = useSelector((state) => state.auth);
  const { countries } = useSelector((state) => state.system);
  const { serviceProvider } = useSelector((state) => state.profile);

  const [current, setCurrent] = useState(accountCreated ? 1 : 0);
  const [submittable, setSubmittable] = useState(false);
  const [disabledInputs, setDisabledInputs] = useState(false);
  const [documentLabel, setDocumentLabel] = useState(
    intl.formatMessage({ id: "id_number_cnpj" })
  );
  const [documentMask, setDocumentMask] = useState("**.***.***/****-**");
  const [taxesByPerson, setTaxesByPerson] = useState(taxes);
  const [defaultCountry, setDefaultCountry] = useState("BR");
  const [defaultCountryMobile, setDefaultCountryMobile] = useState("BR");
  const [selectedOthersServiceType, setSelectedOthersServiceType] =
    useState(false);

  const handleDocumentChange = useCallback(
    (e) => {
      const upperCaseValue = e.target.value.toUpperCase();
      form.setFieldValue("document", upperCaseValue);
      e.target.value = upperCaseValue;
    },
    [],
  );

  const getCepSuccess = useCallback(
    (address) => {
      setDisabledInputs(false);
      form.setFieldsValue({
        address: address.street,
        district: address.neighborhood,
        city: address.city,
        state: address.state,
      });
    },
    [form]
  );

  const handleGoBack = useCallback(() => {
    if (accountCreated) {
      history.goBack();
      return;
    }
    setCurrent(0);
  }, [accountCreated, history]);

  const callBackMessage = useCallback(
    (message, type) => {
      if (type === "success") {
        notification.displaySuccess(
          intl.formatMessage({
            id: "success.registration_data_updated",
          }),
          "change-exhibitor-success"
        );
        history.push("/user/view");
      } else {
        notification.displayError(
          intl.formatMessage({
            id: "api.error." + message,
          }),
          "change-service-provider-error"
        );
      }
      handleLoading(false);
      setDisabledInputs(false);
    },
    [handleGoBack, intl, notification]
  );

  const handleCep = useCallback(
    (cep) => {
      const numericCep = numberMask(cep);

      if (numericCep.length === 8) {
        setDisabledInputs(true);
        dispatch(getCep(numericCep, getCepSuccess, callBackMessage));
      }
    },
    [callBackMessage, getCepSuccess, dispatch]
  );

  const onFinish = () => {
    const formData = form.getFieldsValue(true);

    handleLoading(true);

    const data = {
      type: formData.type,
      companyName: formData.companyName,
      district: formData.district,
      city: formData.city,
      state: formData.state,
      email: formData.email,
      nameLastName: formData.nameLastName,
      name: formData.name,
      state_inscription:
        formData.stateInscription &&
        formData.stateInscription.replace(/[^a-z\d\s]+/gi, ""),
      city_inscription:
        formData.cityInscription &&
        formData.cityInscription.replace(/[^a-z\d\s]+/gi, ""),
      zipCode: formData.zipCode,
      avenue: formData.address,
      country: formData.country.value || formData.country,
      taxes: formData.taxes,
      document: formData.document.replace(/[^a-z\d\s]+/gi, ""),
      address_number: formData.addressNumber,
      provisionTypes: formData.provisionTypes,
      mobile: getFullTelephoneNumber(
        formData.mobile,
        formData.mobile_selected_country.value,
        defaultCountry
      ),
      phone: getFullTelephoneNumber(
        formData.phone,
        formData.phone_selected_country.value,
        defaultCountryMobile
      ),
      othersDescription: formData.othersDescription,
    };

    dispatch(changeProfileServiceProvider(data, callBackMessage));
  };

  const initialValues = useMemo(() => {
    if (serviceProvider?.name) {
      if (serviceProvider?.telephone) {
        setDefaultCountry(
          getCountryFromTelephoneNumber(serviceProvider.telephone).value
        );
      }
      if (serviceProvider?.mobile) {
        setDefaultCountryMobile(
          getCountryFromTelephoneNumber(serviceProvider.mobile).value
        );
      }
      if (serviceProvider?.typesList?.length > 0) {
        setSelectedOthersServiceType(
          serviceProvider.typesList.some((type) => type === OTHERS_SERVICE_TYPE_ID)
        );
      }
      return {
        nameLastName: serviceProvider?.nameLastName,
        name: serviceProvider?.name,
        companyName: serviceProvider?.companyName,
        type: serviceProvider?.accountType,
        document: cnpjCpfFormatter(
          serviceProvider?.document,
          serviceProvider?.accountType
        ),
        stateInscription: serviceProvider?.stateInscription,
        cityInscription: serviceProvider?.municipalInscription,
        country: serviceProvider?.accountAddress?.country,
        address: serviceProvider?.accountAddress?.avenue,
        addressNumber: serviceProvider?.accountAddress?.number,
        zipCode: serviceProvider?.accountAddress?.zipCode,
        district: serviceProvider?.accountAddress?.district,
        city: serviceProvider?.accountAddress?.city,
        state: serviceProvider?.accountAddress?.state,
        phone: removeCountryFromTelephoneNumber(serviceProvider?.telephone),
        mobile: removeCountryFromTelephoneNumber(serviceProvider?.mobile),
        phone_selected_country: getCountryFromTelephoneNumber(
          serviceProvider?.telephone
        ),
        mobile_selected_country: getCountryFromTelephoneNumber(
          serviceProvider?.mobile
        ),
        email: serviceProvider?.email,
        taxes: serviceProvider?.taxes,
        provisionTypes: serviceProvider?.typesList,
        othersDescription: serviceProvider?.othersDescription,
      };
    }

    if (user?.name) {
      return {
        nameLastName: user?.name,
        country: user?.country,
        email: user?.email,
        provisionTypes: [],
      };
    }

    if (user?.email) {
      return {
        email: user?.email,
        provisionTypes: [],
      };
    }

    return INITIAL_VALUES;
  }, [user, serviceProvider]);

  useEffect(() => {
    if (values?.provisionTypes) {
      setSelectedOthersServiceType(
        values.provisionTypes.some((type) => type === OTHERS_SERVICE_TYPE_ID)
      );
    }

    if (values?.type) {
      switch (values?.type) {
        case "F":
          setDocumentLabel(intl.formatMessage({ id: "document" }));
          setDocumentMask("999.999.999-99");
          setTaxesByPerson(taxes[4]);
          return;
        case "J":
          setDocumentLabel(intl.formatMessage({ id: "id_number_cnpj" }));
          setDocumentMask("**.***.***/****-**");
          setTaxesByPerson([taxes[0], taxes[1], taxes[2], taxes[3]]);
          return;
        case "I":
          setDocumentLabel(
            intl.formatMessage({
              id: "passport",
            })
          );
          setDocumentMask("***************");
          setTaxesByPerson([taxes[5]]);
          return;
        default:
          return "";
      }
    }
  }, [values, intl]);

  const steps = useMemo(
    () => [
      {
        title: intl.formatMessage({
          id: "informations",
        }),
        content: (
          <FirstForm
            optionsProvision={optionsProvision}
            taxesByPerson={taxesByPerson}
            types={types}
            countries={countries}
            refForm={form}
            documentLabel={documentLabel}
            documentMask={documentMask}
            selectedOthersServiceType={selectedOthersServiceType}
            handleDocumentChange={handleDocumentChange}
          />
        ),
      },
      {
        title: intl.formatMessage({
          id: "account_data",
        }),
        content: (
          <SecondForm
            handleCep={handleCep}
            refForm={form}
            disabledInputs={disabledInputs}
          />
        ),
      },
    ],
    [
      optionsProvision,
      taxesByPerson,
      countries,
      handleCep,
      form,
      disabledInputs,
      documentMask,
      documentLabel,
      intl,
    ]
  );

  const items = steps.map((item) => ({
    key: item.title,
    title: item.title,
  }));

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmittable(true);
      },
      () => {
        setSubmittable(false);
      }
    );
  }, [form, values]);

  useEffect(() => {
    dispatch(getListCountries());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <Title>
        {intl.formatMessage({
          id: "my_account",
        })}
      </Title>
      <SubTitle>{intl.formatMessage({ id: "general_information" })}</SubTitle>
      <Hr />
      <ContentForm
        layout="vertical"
        form={form}
        name="my-account"
        onFinish={onFinish}
        initialValues={initialValues}
      >
        {accountCreated ? (
          <ChangeForm
            handleCep={handleCep}
            refForm={form}
            disabledInputs={disabledInputs}
            optionsProvision={optionsProvision}
            defaultCountry={defaultCountry}
            defaultCountryMobile={defaultCountryMobile}
            selectedOthersServiceType={selectedOthersServiceType}
          />
        ) : (
          <>
            <CustomSteps
              current={current}
              items={items}
              labelPlacement="vertical"
            />
            <StepsContent>{steps[current].content}</StepsContent>
          </>
        )}
        <StepsAction>
          <ContainerBtn>
            <Button
              label={
                current === 1
                  ? intl.formatMessage({ id: "goback" })
                  : intl.formatMessage({ id: "next" })
              }
              onClick={() => (current === 0 ? setCurrent(1) : handleGoBack())}
              type={current === 0 && submittable ? "primary" : "link"}
              disabled={!submittable}
            />

            {current === 1 && (
              <Button
                label={intl.formatMessage({ id: "save" })}
                type="primary"
                htmlType="submit"
              />
            )}
          </ContainerBtn>
        </StepsAction>
      </ContentForm>
    </Container>
  );
};

export default FormAccountServiceProvider;
